import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import RoutePageLoader from './components/layout/RoutePageLoader';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminProtectedRoute from './components/auth/AdminProtectedRoute';
import useAuthStore from './stores/authStore';
import { useInactivityLogout } from './hooks/useInactivityLogout';

const ProjectsOverviewPage = lazy(() => import('./pages/ProjectsOverviewPage'));
const OverkoepelendProjectHubPage = lazy(() => import('./pages/OverkoepelendProjectHubPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const IntekenPage = lazy(() => import('./pages/IntekenPage'));
const ViewerEntry = lazy(() => import('./pages/ViewerEntry'));
const AdminProjectsOverviewPage = lazy(() => import('./pages/AdminProjectsOverviewPage'));
const AdminKpiPage = lazy(() => import('./pages/AdminKpiPage'));
const CadToGeoJsonPage = lazy(() => import('./pages/CadToGeoJsonPage'));
const WerkinstructiesPage = lazy(() => import('./pages/WerkinstructiesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

const DEFAULT_INACTIVITY_LOGOUT_MINUTES = 240;

function getInactivityLogoutMinutes() {
  const raw = import.meta.env.VITE_INACTIVITY_LOGOUT_MINUTES;
  if (raw == null || raw === '') return DEFAULT_INACTIVITY_LOGOUT_MINUTES;
  const num = Number.parseFloat(raw, 10);
  return Number.isFinite(num) && num > 0 ? num : DEFAULT_INACTIVITY_LOGOUT_MINUTES;
}

function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useInactivityLogout({
    inactivityMinutes: getInactivityLogoutMinutes(),
    onInactive: () => void useAuthStore.getState().logoutAndRedirectToLogin('inactivity'),
    enabled: isAuthenticated,
  });

  return (
    <Suspense fallback={<RoutePageLoader />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="login" element={<LoginPage />} />
          <Route
            path="projects"
            element={
              <ProtectedRoute>
                <ProjectsOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="projects/:id"
            element={
              <ProtectedRoute>
                <OverkoepelendProjectHubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="demarcatiegebieden/:id"
            element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="intekenen"
            element={
              <ProtectedRoute>
                <IntekenPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="cad-naar-geojson"
            element={
              <ProtectedRoute>
                <CadToGeoJsonPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="viewer"
            element={
              <ProtectedRoute>
                <ViewerEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="werkinstructies"
            element={
              <ProtectedRoute>
                <WerkinstructiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="contact"
            element={
              <ProtectedRoute>
                <ContactPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/projects"
            element={
              <AdminProtectedRoute>
                <AdminProjectsOverviewPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="admin/kpi"
            element={
              <AdminProtectedRoute>
                <AdminKpiPage />
              </AdminProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
